import { LockClosedIcon } from '@heroicons/react/solid';
import { Radio, RadioChangeEvent, Tooltip } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Cookies from 'js-cookie';
import moment from 'moment';
import Image from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { cartAtom } from '@/atoms/Cart';
import { countryAtom, getGlobalCountry } from '@/atoms/countryState';
import { getRewardRulesByName } from '@/services/reward';
import { convertCurrency } from '@/utils/currency-convert';

import { ShouldRender } from '../shared/basic/ShouldRender';
import { parseFormattedNumber } from '../shared/parseFormattedNumber';
import { tokenUpdate } from '../../atoms/authModalControl';
import appwriteService from '../../config/appwrite';
import { CardIcons, InternationalCardIcons } from '../../../assets';
import BoltIcon from '../../../assets/Icons/BoltIcon';
import InfoCircleIcon from '../../../assets/Icons/InfoCircle';
import OfferIcon from '../../../assets/Icons/OfferIcon';

interface PaymentOptionsProps {
  selectedOption: string;
  onSelectOption: (e: RadioChangeEvent) => void;
  isCOD_Disabled: boolean;
  handlePaymentOptionClick: (
    value: 'razorpay' | 'cash_on_delivery' | 'wallet' | 'stripe',
  ) => void;
  totalAmount: number;
  isIndia: boolean;
  orderLoading?: boolean;
  walletBalance?: number;
  walletDisable?: boolean;
  notRenderWallet?: boolean;
  isHardCopy?: boolean;
  isCodDisabledForCountry?: boolean;
}

const PaymentInfo: React.FC<PaymentOptionsProps> = ({
  selectedOption,
  onSelectOption,
  isCOD_Disabled,
  handlePaymentOptionClick,
  totalAmount,
  orderLoading,
  walletBalance,
  notRenderWallet = false,
  walletDisable,
  isHardCopy,
  isCodDisabledForCountry,
}) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [cashBack, setCashBack] = useState<any>(0);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const totalCartAmount = parseFormattedNumber(totalAmount?.toString());
  const allowedAmount = process.env.NEXT_PUBLIC_ALLOWABLE_COD_AMOUNT || 12000;
  const [rate, setRate] = useState<number | null>(null);
  const cartData = useRecoilValue(cartAtom);
  const [countryState] = useRecoilState(countryAtom);

  const CASHBACK_COOKIE_KEY = 'cashback';
  const screens = useBreakpoint();
  const isPreOrder = useMemo(() => {
    return cartData?.some((item) => item?.is_preorder === true);
  }, [cartData]);
  const globalCountry = getGlobalCountry();

  const isCODUnavailable = useMemo(() => {
    return (
      isCOD_Disabled ||
      totalCartAmount > Number(allowedAmount) ||
      isPreOrder ||
      isCodDisabledForCountry
    );
  }, [
    isCOD_Disabled,
    totalCartAmount,
    allowedAmount,
    isPreOrder,
    isCodDisabledForCountry,
  ]);

  const fetchRate = async () => {
    const fromCurrency = 'INR';
    const toCurrency = globalCountry?.currency as string;

    if (fromCurrency === toCurrency) {
      setRate(1);
      return;
    }

    const cacheKey = `rate_${fromCurrency}_${toCurrency}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached && cached !== 'undefined' && Number(cached)) {
      setRate(Number(cached));
      return;
    }

    try {
      const result = await convertCurrency(fromCurrency, toCurrency, 1);

      if (result) {
        localStorage.setItem(cacheKey, result.toString());
        setRate(result);
      }
    } catch (error) {
      /* empty */
    }
  };

  useEffect(() => {
    fetchRate();
  }, [globalCountry?.currency]);

  const codUnavailableMessage = useMemo(() => {
    const unavailableProducts = cartData
      ?.filter((item) => item.cash_on_delivery === false)
      .map((item) => item.name);

    if (!unavailableProducts || unavailableProducts.length === 0) return null;

    const countryName = getGlobalCountry().name;

    const message =
      unavailableProducts.length > 1
        ? `COD not available for ${unavailableProducts.join(', ')} in ${countryName}.`
        : `COD not available for ${unavailableProducts[0]} in ${countryName}.`;

    return (
      <p className='text-[10px] md:text-sm text-grayZl-700 pl-3 mt-2'>
        <b>•&#160;</b>
        {message}
      </p>
    );
  }, [cartData, getGlobalCountry]);

  const handleCashbackCoin = async (setCoins: (coins: any) => void) => {
    try {
      const cached = Cookies.get(CASHBACK_COOKIE_KEY);

      if (cached) {
        const parsed = JSON.parse(cached);
        const now = moment();
        const expiry = moment(parsed.expiry);

        if (now.isBefore(expiry)) {
          setCoins(parsed.data);
          return;
        }
      }

      const data = await getRewardRulesByName(CASHBACK_COOKIE_KEY);
      setCoins(data.data);

      const expiryTime = moment().add(6, 'hours').toISOString();

      Cookies.set(
        CASHBACK_COOKIE_KEY,
        JSON.stringify({
          data: data.data,
          expiry: expiryTime,
        }),
        { expires: 1 },
      );
    } catch (error: any) {
      /* empty */
    }
  };

  useEffect(() => {
    handleCashbackCoin(setCashBack);
  }, []);

  const isWalletDisabled =
    walletDisable || (walletBalance || 0) < totalCartAmount;

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  const offers = [
    {
      text: (
        <span className=' text-[9px] xs:text-[11px]  uppercase text-white md:text-sm font-medium'>
          Instant Digital Access
        </span>
      ),
      image: <BoltIcon />,
      type: 'icon',
      check: true,
    },
    {
      text: (
        <span className=' text-[9px] xs:text-[11px]  uppercase text-white  md:text-sm font-medium'>
          Get{' '}
          <p className='text-xs md:text-[15px] inline-flex px-0.5'>
            {cashBack} Z coins as
          </p>{' '}
          cashback
        </span>
      ),
      image: <OfferIcon />,
      type: 'icon',
      check: countryState.selectedCountry?.code === 'IN',
    },
  ];
  return (
    <div className='shadow-md space-y-4 w-full text-white'>
      <div className='space-y-2 w-full'>
        <motion.div
          className={clsx(
            selectedOption === 'razorpay' ? 'pb-6 md:pb-7 ' : 'pb-5',
            `pt-6 px-2 xxxs:px-4 md:px-6 rounded-lg cursor-pointer hover:bg-[#101010] w-full ${
              selectedOption === 'razorpay' ? 'bg-[#101010]' : ''
            }`,
          )}
          onClick={() => !orderLoading && handlePaymentOptionClick('razorpay')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          whileHover={{ scale: 1 }}
          whileTap={{ scale: 0.99 }}
        >
          <div className='flex items-start w-full '>
            <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
              <Radio
                value='razorpay'
                checked={selectedOption === 'razorpay'}
                onChange={onSelectOption}
                className='scale-125 translate-y-1'
              />
            </ShouldRender>
            <div className='flex flex-col  md:ml-2.5 w-full '>
              <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
                <div
                  className={clsx(
                    ' flex translate-y-[2px] md:translate-y-0 w-full',
                  )}
                >
                  <div
                    className={clsx(
                      ' w-fit relative cursor-pointer overflow-hidden',
                    )}
                  >
                    {/* Shimmer Effect */}
                    <motion.div
                      className='absolute top-0 left-0 z-30 cursor-pointer h-full   w-full '
                      style={{
                        background:
                          'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)',
                        transform: 'translateX(-100%)',
                      }}
                      animate={{ translateX: ['-100%', '100%'] }}
                      transition={{
                        duration: 2,
                        ease: 'linear',
                        repeat: Infinity,
                      }}
                    />

                    {/* Button */}

                    <div
                      style={{
                        background:
                          'linear-gradient(90deg, #0380F3 0%, #024A8D 100%)',
                      }}
                      className='text-white text-[10px] z-20   rounded-[4px]  px-2.5  py-[4px] font-semibold  '
                    >
                      Most Used
                    </div>
                  </div>
                </div>
              </ShouldRender>
              <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
                <h3
                  className={clsx(
                    'text-xs md:text-sm font-normal md:font-medium leading-6 mt-3',
                  )}
                >
                  Pre-Paid (UPI, Credit card, Debit card)
                </h3>
              </ShouldRender>
              <span className='text-xs flex  gap-x-1.5 items-center text-[#646464]'>
                <span className=''>
                  <LockClosedIcon className='w-3.5 h-3.5 -translate-y-[1px] text-grayZl-600' />
                </span>
                All transactions are secure and encrypted
              </span>
              <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
                <div className='flex h-[18px] max-w-[320px] mt-3 pr-20'>
                  <Image
                    src={CardIcons}
                    alt='OnlinePayment'
                    className='object-fill w-full '
                  />
                </div>
              </ShouldRender>
              <ShouldRender check={countryState.selectedCountry?.code !== 'IN'}>
                <div className='flex  w-full  max-w-[320px] mt-3 pr-20'>
                  <Image
                    src={InternationalCardIcons}
                    alt='OnlinePayment'
                    className='object-fill w-auto '
                  />
                </div>
              </ShouldRender>

              <ul className='list-disc mt-4 flex flex-col gap-y-2'>
                {offers.map((offer, index) => (
                  <ShouldRender
                    key={`${offer?.text}-${index}`}
                    check={offer.check}
                  >
                    <li className='flex border-[#FFFFFF0D] border-[1px] rounded-lg w-fit bg-[#FFFFFF0D] list-disc py-1.5 px-2.5 items-center  space-x-1 md:space-x-2'>
                      {offer.image}
                      <div className='flex gap-x-1 md:gap-x-2 items-center text-[#A7A7A7]'>
                        {offer.text}
                      </div>
                      <ShouldRender check={index === 1}>
                        <Tooltip
                          title={`10 Z coin = ${globalCountry?.currency_symbol}
                ${rate ? rate : '...'}`}
                        >
                          <span>
                            <InfoCircleIcon width={!screens.md ? '13' : '18'} />
                          </span>
                        </Tooltip>
                      </ShouldRender>
                    </li>
                  </ShouldRender>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
        <ShouldRender
          check={
            !isCodDisabledForCountry ||
            globalCountry?.name?.toLowerCase() === 'india'
          }
        >
          <motion.div
            className={clsx(
              selectedOption === 'cash_on_delivery' ? 'pb-5' : 'pb-5',
              isCODUnavailable && 'cursor-not-allowed text-gray-200',
              `pt-6 px-4 md:px-6 rounded-lg cursor-pointer hover:bg-[#101010] ${
                selectedOption === 'cash_on_delivery' ? 'bg-[#101010]' : ''
              }`,
            )}
            onClick={() =>
              !isCODUnavailable &&
              !orderLoading &&
              handlePaymentOptionClick('cash_on_delivery')
            }
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            whileHover={{ scale: 1 }}
            whileTap={{ scale: 0.99 }}
          >
            <div
              className={clsx(
                isCODUnavailable && 'text-gray-300 cursor-not-allowed',
                'flex items-start',
              )}
            >
              <Radio
                value='cash_on_delivery'
                checked={selectedOption === 'cash_on_delivery'}
                onChange={onSelectOption}
                disabled={isCODUnavailable}
                className='scale-105 translate-y-0.5'
              />
              <div
                className={clsx(
                  isCODUnavailable && 'text-grayZl-700 cursor-not-allowed',
                  'mt-[1px]  md:mt-0.5 md:ml-2.5',
                )}
              >
                <h3
                  className={clsx(
                    selectedOption === 'cash_on_delivery'
                      ? 'text-white'
                      : 'text-grayZl-700 ',
                    'text-xs pl-1 md:pl-0 md:text-base font-medium leading-6',
                  )}
                >
                  Cash on delivery (COD)
                </h3>
                {/* <ShouldRender check={selectedOption === 'cash_on_delivery'}>
                <div>
                  <span className='bg-[#022342] w-fit rounded-md flex justify-center mt-3.5'>
                    <span className='font-semibold uppercase text-[10px] xxxs:text-xs px-2 xxxs:px-2.5 py-1.5 tracking-wider whitespace-nowrap text-[#0380F3] '>
                      Digital Access AFTER DELIVERY
                    </span>
                  </span>
                </div>
              </ShouldRender> */}

                <ShouldRender check={isCOD_Disabled}>
                  <span>
                    <p className='text-[10px] md:text-sm text-grayZl-700  pl-3 mt-2'>
                      <b>•&#160;</b> Cash on Delivery is unavailable as digital
                      items are present in your cart.
                    </p>
                  </span>
                </ShouldRender>
                {/* <ShouldRender check={isIndia}>
                <span>
                  <p className='text-[10px] md:text-sm text-grayZl-600 pl-3 mt-2'>
                    <b>•&#160;</b> COD not available for locations outside
                    India.
                  </p>
                </span>
              </ShouldRender> */}
                <ShouldRender check={isCodDisabledForCountry}>
                  {codUnavailableMessage}
                </ShouldRender>

                <ShouldRender check={totalCartAmount > Number(allowedAmount)}>
                  <span>
                    <p className='text-[10px] md:text-sm text-grayZl-700  pl-3 mt-2'>
                      <b>•&#160;</b> COD unavailable for cart amount above{' '}
                      {Number(allowedAmount)}
                    </p>
                  </span>
                </ShouldRender>
                <ShouldRender
                  check={selectedOption === 'cash_on_delivery' && isHardCopy}
                >
                  <p className='text-[10px] md:text-sm text-grayZl-700 pl-3 mt-1 md:mt-2'>
                    <b>•&#160;</b> Digital access after delivery
                  </p>
                </ShouldRender>
              </div>
            </div>
          </motion.div>
        </ShouldRender>
        <ShouldRender check={loggedIn && !notRenderWallet}>
          <motion.div
            className={clsx(
              'pt-6 px-4 md:px-6 rounded-lg cursor-pointer transition-all duration-200',
              selectedOption === 'wallet'
                ? 'bg-[#101010]'
                : 'hover:bg-[#101010]',
              isWalletDisabled ? 'cursor-not-allowed text-gray-300' : '',
              'pb-5',
            )}
            onClick={() => {
              if (!isWalletDisabled && !orderLoading)
                handlePaymentOptionClick('wallet');
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            whileHover={{ scale: isWalletDisabled ? 1 : 1.02 }}
            whileTap={{ scale: isWalletDisabled ? 1 : 0.98 }}
            role='button'
            aria-disabled={isWalletDisabled}
          >
            <div className='flex items-start'>
              <Radio
                value='wallet'
                checked={selectedOption === 'wallet'}
                onChange={onSelectOption}
                disabled={isWalletDisabled}
                className='custom-radio-wrapper'
                aria-label='Select Wallet Transfer'
              />
              <div className='md:ml-2.5 flex-1'>
                <div className='flex justify-between w-full gap-x-2'>
                  <h3 className='text-sm pl-1 md:pl-0 md:text-base font-medium leading-6'>
                    Wallet Transfer
                  </h3>
                  <ShouldRender check={walletBalance}>
                    <div className='rounded-md  flex gap-x-3 items-center text-right'>
                      <p className='text-xs md:text-sm font-medium text-[#A7A7A7]'>
                        Balance:
                      </p>
                      <p className='text-base md:text-lg font-semibold text-[#009C63]'>
                        ₹{walletBalance?.toFixed(2)}
                      </p>
                    </div>
                  </ShouldRender>
                </div>
                {isWalletDisabled && (
                  <ul className='text-xs md:text-sm text-grayZl-700 pl-3 mt-2 list-disc'>
                    <li>
                      {walletBalance
                        ? `Insufficient wallet balance. Your wallet balance is ₹${walletBalance}, which is less than the total amount of ₹${totalCartAmount}.`
                        : 'Wallet balance unavailable.'}
                    </li>
                  </ul>
                )}
                {!isWalletDisabled && (
                  <div className='mt-3'>
                    <span className='bg-[#00281A] rounded-md inline-flex items-center justify-center px-2.5 py-1.5'>
                      <span className='text-[10px] xxxs:text-xs font-semibold uppercase tracking-wide text-[#009C63]'>
                        No Additional Charges
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </ShouldRender>
      </div>
    </div>
  );
};

export default PaymentInfo;
